import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CorrelationCalculator = () => {
  const [inputData, setInputData] = useState({
    xValues: '',
    yValues: '',
    useSample: true
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateCorrelation = () => {
    const { xValues, yValues, useSample } = inputData;

    // Validate inputs
    if (!xValues || !yValues) {
      setError('Please enter values for both X and Y');
      return;
    }

    try {
      // Parse input values
      const xArray = xValues.split(',').map(val => parseFloat(val.trim()));
      const yArray = yValues.split(',').map(val => parseFloat(val.trim()));

      if (xArray.some(isNaN) || yArray.some(isNaN)) {
        setError('Please enter valid numbers for both X and Y');
        return;
      }

      if (xArray.length !== yArray.length) {
        setError('X and Y must have the same number of elements');
        return;
      }

      if (xArray.length < 2) {
        setError('At least two data points are required for correlation calculation');
        return;
      }

      // Calculate means
      const xMean = xArray.reduce((a, b) => a + b, 0) / xArray.length;
      const yMean = yArray.reduce((a, b) => a + b, 0) / yArray.length;

      // Calculate covariance and standard deviations
      let covariance = 0;
      let xStdDevSum = 0;
      let yStdDevSum = 0;

      for (let i = 0; i < xArray.length; i++) {
        const xDiff = xArray[i] - xMean;
        const yDiff = yArray[i] - yMean;
        covariance += xDiff * yDiff;
        xStdDevSum += xDiff * xDiff;
        yStdDevSum += yDiff * yDiff;
      }

      const divisor = useSample ? xArray.length - 1 : xArray.length;
      const xStdDev = Math.sqrt(xStdDevSum / divisor);
      const yStdDev = Math.sqrt(yStdDevSum / divisor);
      const correlation = covariance / (divisor * xStdDev * yStdDev);

      // Create visualization data
      const chartData = {
        labels: xArray.map((_, i) => `Point ${i + 1}`),
        datasets: [
          {
            label: 'X Values',
            data: xArray,
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          },
          {
            label: 'Y Values',
            data: yArray,
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1
          }
        ]
      };

      // Create calculation steps
      const calculationSteps = [
        `Step 1: Calculate the mean of X: ${xMean.toFixed(4)}`,
        `Step 2: Calculate the mean of Y: ${yMean.toFixed(4)}`,
        `Step 3: Calculate covariance: ${covariance.toFixed(4)}`,
        `Step 4: Calculate standard deviation of X: ${xStdDev.toFixed(4)}`,
        `Step 5: Calculate standard deviation of Y: ${yStdDev.toFixed(4)}`,
        `Step 6: Calculate correlation coefficient: ${correlation.toFixed(4)}`
      ];

      setResult({
        xValues: xArray,
        yValues: yArray,
        xMean: xMean.toFixed(4),
        yMean: yMean.toFixed(4),
        covariance: covariance.toFixed(4),
        xStdDev: xStdDev.toFixed(4),
        yStdDev: yStdDev.toFixed(4),
        correlation: correlation.toFixed(4),
        calculationSteps,
        chartData
      });

    } catch (err) {
      setError('Error calculating correlation: ' + err.message);
    }
  };

  const exampleData = {
    xValues: '10,34,23,54,9',
    yValues: '4,5,11,15,20',
    correlation: '0.0455'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Correlation Calculator | Calculate Pearson Correlation Coefficient</title>
          <meta name="description" content="Calculate the Pearson correlation coefficient between two datasets. This calculator provides detailed calculation steps and visualizations to help understand the correlation between variables." />
          <meta name="keywords" content="correlation calculator, pearson correlation, correlation coefficient, statistics calculator, data analysis" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-purple-600 mr-4">📊</div>
            <h1 className="text-3xl font-bold text-gray-800">Correlation Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the Pearson correlation coefficient between two datasets. This coefficient measures the linear relationship between two variables, ranging from -1 (perfect negative correlation) to 1 (perfect positive correlation).</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">X Values:</label>
              <input
                type="text"
                name="xValues"
                value={inputData.xValues}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter X values separated by commas (e.g., 1,2,3,4,5)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Y Values:</label>
              <input
                type="text"
                name="yValues"
                value={inputData.yValues}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter Y values separated by commas (e.g., 5,4,3,2,1)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <input
                  type="checkbox"
                  name="useSample"
                  checked={inputData.useSample}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                Use sample standard deviation (uncheck for population)
              </label>
            </div>
          </div>

          <button
            onClick={calculateCorrelation}
            className="w-full py-3 px-4 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Correlation
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Correlation Summary</h3>
                  <p className="text-lg">X Mean: {result.xMean}</p>
                  <p className="text-lg">Y Mean: {result.yMean}</p>
                  <p className="text-lg">Covariance: {result.covariance}</p>
                  <p className="text-lg">X Standard Deviation: {result.xStdDev}</p>
                  <p className="text-lg">Y Standard Deviation: {result.yStdDev}</p>
                  <p className="text-2xl font-bold text-purple-600 mt-4">Correlation Coefficient: {result.correlation}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Data Visualization</h3>
                  <div className="h-64 bg-white rounded-lg">
                    <Bar
                      data={result.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Values'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Data Points'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'X and Y Values'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {result.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-purple-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for the correlation coefficient:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>X Values: {exampleData.xValues}</li>
                <li>Y Values: {exampleData.yValues}</li>
                <li>Correlation Coefficient: {exampleData.correlation}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Calculate the mean of X and Y</li>
                <li>Calculate the covariance between X and Y</li>
                <li>Calculate the standard deviations of X and Y</li>
                <li>Divide the covariance by the product of standard deviations</li>
                <li>The result is the correlation coefficient</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-purple-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Correlation Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Data:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input X values separated by commas</li>
                  <li>Input Y values separated by commas</li>
                  <li>Ensure both datasets have the same number of elements</li>
                </ul>
              </li>
              <li><strong>Select Calculation Type:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Check the box for sample standard deviation (default)</li>
                  <li>Uncheck for population standard deviation</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The means of X and Y</li>
                  <li>The covariance between X and Y</li>
                  <li>The standard deviations of X and Y</li>
                  <li>The correlation coefficient</li>
                  <li>A visual representation of the data</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-purple-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>A correlation close to 1 indicates a strong positive relationship</li>
                <li>A correlation close to -1 indicates a strong negative relationship</li>
                <li>A correlation near 0 indicates no linear relationship</li>
                <li>Correlation does not imply causation</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Correlation <span className="text-purple-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              The Pearson correlation coefficient measures the linear relationship between two variables. It ranges from -1 to 1:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>1:</strong> Perfect positive linear relationship</li>
              <li><strong>0:</strong> No linear relationship</li>
              <li><strong>-1:</strong> Perfect negative linear relationship</li>
            </ul>
            <p className="mt-4">
              Correlation is widely used in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Finance:</strong> Portfolio diversification and risk assessment</li>
                <li><strong>Research:</strong> Identifying relationships between variables</li>
                <li><strong>Business:</strong> Market analysis and forecasting</li>
                <li><strong>Healthcare:</strong> Studying relationships between variables in medical research</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-purple-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Check Data Quality:</strong> Ensure your data is accurate and representative</li>
              <li><strong>Understand Limitations:</strong> Correlation does not imply causation</li>
              <li><strong>Use Scatter Plots:</strong> Visualize relationships before calculating correlation</li>
              <li><strong>Consider Non-linear Relationships:</strong> Correlation only measures linear relationships</li>
            </ul>
            <p className="italic mt-4">
              Note: For non-linear relationships, consider other statistical methods like Spearman's rank correlation.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CorrelationCalculator;